import { WorkoutPhysiologyPanels } from '../../components/WorkoutPhysiologyPanels';
import { FitAdditionalMetrics } from '../../components/FitAdditionalMetrics';
import { WorkoutAnnualRecords } from '../../components/WorkoutAnnualRecords';
import { isRunningWorkout } from '../../utils/runningWorkoutMetrics';
import { RunningWorkoutStatistics } from '../../components/RunningWorkoutStatistics';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { WorkoutDetail } from '../../services/api';
import { secToHMS } from '../../utils/secToHMS';
import { buildComparisonRows, buildHeroMetrics, buildStatsRows } from './workoutSummaryHelpers';
import {
  WsComparisonTable,
  WsHeroCard,
  WsSectionCard,
  WsStatsTable,
} from './WorkoutSummaryComponents';
import { WorkoutStructureBarPreview } from './WorkoutStructureBarPreview';
import { type StructureStepThresholds } from './WorkoutStructureSteps';
import { MobileWorkoutLapsTab } from './MobileWorkoutLapsTab';
import { WorkoutFeelingModal, type WorkoutFeelingPayload } from './WorkoutFeelingModal';
import { WorkoutTypePickerModal } from './form/WorkoutTypePickerModal';
import { WorkoutTypeIconTile } from '../../components/WorkoutTypeIconTile';
import { NmTabNav } from '../../components/NmTabNav';
import { useAthleteMetrics } from '../../hooks/useAthleteMetrics';
import type { WorkoutType } from '../../services/api';
import {
  hasWorkoutFeelingRecorded,
  isCompletedWorkout,
  parseWorkoutFeeling,
  parseWorkoutRpe,
  shouldAutoPromptWorkoutFeeling,
  WORKOUT_FEELING_EMOJIS,
  WORKOUT_FEELING_LABEL_KEYS,
} from './workoutFeelingConstants';
import { logWorkoutFeeling } from './workoutFeelingDebug';

interface Props {
  data: WorkoutDetail;
  autoPromptFeeling?: boolean;
  userId?: number;
  intensityThresholds?: StructureStepThresholds;
  onEditStructure?: () => void;
  onCommentSubmit?: (text: string) => Promise<void> | void;
  onDeleteComment?: (commentId: number) => Promise<void> | void;
  onFeelingSave?: (payload: WorkoutFeelingPayload) => Promise<void> | void;
  /** Instant sport-type update (no Save button) — PATCH then parent reload. */
  onSportTypeChange?: (typeId: number) => Promise<void>;
  /** Retained for callers; the summary no longer renders notes or comments. */
  showCommentInvite?: boolean;
}

type SummaryInsightTab = 'comparison' | 'sensors' | 'laps';

export function MobileWorkoutSummaryTab({
  data,
  autoPromptFeeling = true,
  userId,
  intensityThresholds: intensityThresholdsProp,
  onEditStructure,
  onFeelingSave,
  onSportTypeChange,
}: Props) {
  const { t } = useTranslation();
  const metricsFromHook = useAthleteMetrics(intensityThresholdsProp ? null : (userId ?? null));
  const intensityThresholds: StructureStepThresholds = intensityThresholdsProp ?? {
    ftp: metricsFromHook.ftp,
    ltHr: metricsFromHook.ltHr,
    maxHr: metricsFromHook.maxHr,
    thresholdSpeedMps: metricsFromHook.thresholdSpeedMps,
  };
  const { ftp, ltHr, maxHr, thresholdSpeedMps } = intensityThresholds;
  const [feelModalOpen, setFeelModalOpen] = useState(false);
  const [feelingSaving, setFeelingSaving] = useState(false);
  const [typePickerOpen, setTypePickerOpen] = useState(false);
  const [typeSaving, setTypeSaving] = useState(false);
  const [activeInsightTab, setActiveInsightTab] = useState<SummaryInsightTab>('comparison');
  const [insightSlideDirection, setInsightSlideDirection] = useState<'forward' | 'backward'>('forward');
  const [feeling, setFeeling] = useState<number | null>(() => parseWorkoutFeeling(data?.feeling));
  const [rpe, setRpe] = useState<number | null>(() => parseWorkoutRpe(data?.rpe));
  const autoPromptedWorkoutIdRef = useRef<number | null>(null);

  useEffect(() => {
    setFeeling(parseWorkoutFeeling(data?.feeling));
    setRpe(parseWorkoutRpe(data?.rpe));
  }, [data?.feeling, data?.rpe, data?.id]);

  /** Ask once per panel visit when summary opens for a completed workout without feeling/RPE. */
  useEffect(() => {
    const workoutId = data?.id;
    const snapshot = {
      workoutId,
      total_time: data?.total_time,
      tss_actual: data?.tss_actual,
      feeling: data?.feeling,
      rpe: data?.rpe,
      hasOnFeelingSave: !!onFeelingSave,
      isCompleted: isCompletedWorkout(data),
      hasRecorded: hasWorkoutFeelingRecorded(data),
      shouldPrompt: shouldAutoPromptWorkoutFeeling(data),
      alreadyPromptedId: autoPromptedWorkoutIdRef.current,
    };

    if (!autoPromptFeeling || !onFeelingSave || !workoutId) {
      logWorkoutFeeling('auto-prompt skipped: no handler or id', snapshot);
      return;
    }
    if (!shouldAutoPromptWorkoutFeeling(data)) {
      logWorkoutFeeling('auto-prompt skipped: not eligible', snapshot);
      return;
    }
    if (autoPromptedWorkoutIdRef.current === workoutId) {
      logWorkoutFeeling('auto-prompt skipped: already prompted this visit', snapshot);
      return;
    }

    logWorkoutFeeling('auto-prompt scheduled (320ms)', snapshot);
    const timer = window.setTimeout(() => {
      autoPromptedWorkoutIdRef.current = workoutId;
      setFeelModalOpen(true);
      logWorkoutFeeling('auto-prompt opened modal', { workoutId });
    }, 320);
    return () => {
      window.clearTimeout(timer);
      logWorkoutFeeling('auto-prompt timer cleared (effect cleanup)', { workoutId });
    };
  }, [data?.id, data?.total_time, data?.tss_actual, data?.feeling, data?.rpe, onFeelingSave, data, autoPromptFeeling]);

  const displayTotalTime =
    data?.total_time != null
      ? Number(data.total_time)
      : data?.total_time_planned != null
        ? Number(data.total_time_planned)
        : null;

  const displayTss =
    data?.training_load_method === 'pwr_quarantined' ? null : data?.tss_actual != null
      ? Number(data.tss_actual)
      : data?.tss_planned != null
        ? Number(data.tss_planned)
        : null;

  const heroMetrics = useMemo(() => buildHeroMetrics(data, displayTss), [data, displayTss]);
  const heroSubjectiveMetrics = useMemo(() => {
    const items: Array<{ key: 'feeling' | 'rpe'; value: string; label: string; isEmoji?: boolean }> = [];
    if (feeling != null && feeling >= 1 && feeling <= WORKOUT_FEELING_EMOJIS.length) {
      items.push({
        key: 'feeling',
        value: WORKOUT_FEELING_EMOJIS[feeling - 1],
        label: t(WORKOUT_FEELING_LABEL_KEYS[feeling - 1]),
        isEmoji: true,
      });
    }
    if (rpe != null) {
      items.push({
        key: 'rpe',
        value: String(rpe),
        label: t('workoutDetail.summary.heroRpe'),
      });
    }
    return items;
  }, [feeling, rpe, t]);
  const comparisonRows = useMemo(() => buildComparisonRows(data), [data]);
  // The summary ends the additional sensor readings at elevation.
  const summaryFitMetrics = useMemo(() => {
    const snapshot = data.details?.fit_metrics;
    return snapshot ? { ...snapshot, metrics: snapshot.metrics.filter(metric => metric.name === 'altitude') } : null;
  }, [data.details?.fit_metrics]);
  const statsRows = useMemo(() => {
    const mma = data?.details?.min_max_avg;
    const recorded = data?.details?.fit_metrics?.metrics?.find(metric => metric.name === 'respiration_rate');
    return buildStatsRows({ ...mma, respiration_rate: {
      min: mma?.respiration_rate?.min ?? recorded?.min,
      avg: mma?.respiration_rate?.avg ?? recorded?.avg,
      max: mma?.respiration_rate?.max ?? recorded?.max,
    } });
  }, [data?.details?.min_max_avg, data?.details?.fit_metrics]);
  const hasStructure = !!(data?.structure?.structure?.length);
  const hasLaps = !!(data?.details?.lap_data?.length);
  const insightTabs = useMemo(
    () => [
      comparisonRows.length > 0
        ? { key: 'comparison' as const, label: t('workoutDetail.summary.comparisonTitle') }
        : null,
      (statsRows.length > 0 || !!data?.details?.fit_metrics)
        ? { key: 'sensors' as const, label: t('workoutDetail.summary.statsTitle') }
        : null,
      hasLaps
        ? { key: 'laps' as const, label: t('workoutDetail.summary.lapsTab') }
        : null,
    ].filter((item): item is { key: SummaryInsightTab; label: string } => item != null),
    [comparisonRows.length, hasLaps, statsRows.length, data?.details?.fit_metrics, t],
  );

  useEffect(() => {
    if (insightTabs.some((tab) => tab.key === activeInsightTab)) return;
    if (insightTabs[0]) setActiveInsightTab(insightTabs[0].key);
  }, [activeInsightTab, insightTabs]);

  const handleInsightTabChange = (nextKey: SummaryInsightTab) => {
    const currentIndex = insightTabs.findIndex((tab) => tab.key === activeInsightTab);
    const nextIndex = insightTabs.findIndex((tab) => tab.key === nextKey);
    setInsightSlideDirection(nextIndex >= currentIndex ? 'forward' : 'backward');
    setActiveInsightTab(nextKey);
  };

  const handleFeelingSave = async (payload: WorkoutFeelingPayload) => {
    if (!onFeelingSave) return;
    setFeelingSaving(true);
    try {
      await onFeelingSave(payload);
      setFeeling(payload.feeling);
      setRpe(payload.rpe);
      setFeelModalOpen(false);
    } finally {
      setFeelingSaving(false);
    }
  };

  const sportType = data?.workout_type;
  const sportName = sportType?.english_name || sportType?.name || '';

  const handleSportTypeSelect = async (item: WorkoutType) => {
    if (!onSportTypeChange) {
      setTypePickerOpen(false);
      return;
    }
    if (item.id === sportType?.id) {
      setTypePickerOpen(false);
      return;
    }
    setTypeSaving(true);
    try {
      await onSportTypeChange(item.id);
      setTypePickerOpen(false);
    } finally {
      setTypeSaving(false);
    }
  };

  const sportTile = (
    <WorkoutTypeIconTile typeId={sportType?.id} name={sportName || 'Other'} size="md" />
  );

  return (
    <div className="ws-summary ws-summary--centered">
      <WorkoutAnnualRecords records={data.annual_records} />
      <WsHeroCard
        eyebrow={data?.title || undefined}
        duration={displayTotalTime ? secToHMS(displayTotalTime) : '—'}
        metrics={heroMetrics}
        subjectiveMetrics={heroSubjectiveMetrics}
        onSubjectiveClick={onFeelingSave ? () => setFeelModalOpen(true) : undefined}
        sportIcon={
          sportName || onSportTypeChange ? (
            onSportTypeChange ? (
              <button
                type="button"
                className="ws-hero__sport-btn"
                onClick={() => setTypePickerOpen(true)}
                disabled={typeSaving}
                aria-label={t('workoutDetail.summary.changeSport')}
                title={t('workoutDetail.summary.changeSport')}
              >
                {sportTile}
              </button>
            ) : (
              sportTile
            )
          ) : undefined
        }
      />

      {onSportTypeChange ? (
        <WorkoutTypePickerModal
          open={typePickerOpen}
          selectedId={sportType?.id ?? null}
          onSelect={(item) => { void handleSportTypeSelect(item); }}
          onClose={() => {
            if (!typeSaving) setTypePickerOpen(false);
          }}
        />
      ) : null}

      <WorkoutPhysiologyPanels snapshot={data.details?.fit_metrics} mode="summary" />

      {hasStructure && (
        <WsSectionCard
          title={t('workoutDetail.summary.structureTitle')}
          subtitle={t('workoutDetail.summary.structureTapEdit')}
          onClick={onEditStructure}
        >
          <div className="ws-structure-preview">
            <WorkoutStructureBarPreview
              structure={data.structure}
              intensityThresholds={{ ftp, ltHr, maxHr, thresholdSpeedMps }}
            />
          </div>
        </WsSectionCard>
      )}

      {insightTabs.length > 0 && (
        <section className="ws-insights-tabs" aria-label={t('workoutDetail.summary.insightTabsLabel')}>
          <NmTabNav
            items={insightTabs.map((tab) => ({ value: tab.key, label: tab.label }))}
            value={activeInsightTab}
            onChange={handleInsightTabChange}
            ariaLabel={t('workoutDetail.summary.insightTabsLabel')}
            idBase="ws-insight-tab"
            panelId="ws-insight-panel"
          />
          <div className="ws-insights-tabs__viewport">
            <div
              key={activeInsightTab}
              id="ws-insight-panel"
              className={`ws-insights-tabs__panel ws-insights-tabs__panel--${insightSlideDirection}`}
              role="tabpanel"
              aria-labelledby={`ws-insight-tab-${activeInsightTab}`}
            >
              {activeInsightTab === 'comparison' && <WsComparisonTable rows={comparisonRows} />}
              {activeInsightTab === 'sensors' && <>{isRunningWorkout(data) ? <RunningWorkoutStatistics workout={data} /> : <WsStatsTable rows={statsRows} />}<FitAdditionalMetrics workout={data} snapshot={summaryFitMetrics} hideHeaders /></>}
              {activeInsightTab === 'laps' && <MobileWorkoutLapsTab data={data} />}
            </div>
          </div>
        </section>
      )}

      {onFeelingSave && (
        <WorkoutFeelingModal
          open={feelModalOpen}
          initialFeeling={feeling}
          initialRpe={rpe}
          saving={feelingSaving}
          onClose={() => setFeelModalOpen(false)}
          onSave={handleFeelingSave}
        />
      )}

    </div>
  );
}
